<template>
  <Card>
    <div slot="body">
      <div class="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h4 class="mb-1">เสร็จสิ้น</h4>
          <p class="text-muted mb-0">ออเดอร์ถูกสร้างเรียบร้อยแล้ว</p>
        </div>
        <Button v-if="ordersWithTracking.length > 0"
                type="button"
                variant="success"
                size="sm"
                @click="exportToExcel">
          ดาวน์โหลด Excel
        </Button>
      </div>

      <!-- Summary -->
      <div class="alert alert-info text-center mb-4">
        <h5>สรุปผลการสร้างออเดอร์</h5>
        <div class="row mt-3">
          <div class="col-md-4">
            <div class="h3 text-success mb-0">{{ successResults.length }}</div>
            <div>สำเร็จ</div>
          </div>
          <div class="col-md-4">
            <div class="h3 text-danger mb-0">{{ errorResults.length }}</div>
            <div>ผิดพลาด</div>
          </div>
          <div class="col-md-4">
            <div class="h3 text-primary mb-0">{{ totalResults }}</div>
            <div>ทั้งหมด</div>
          </div>
        </div>
      </div>

      <!-- Success Results -->
      <div v-if="successResults.length > 0">
        <h5 class="text-success mb-3">ออเดอร์ที่สร้างสำเร็จ</h5>
        <div v-for="(result, index) in successResults"
             :key="'success-' + index"
             class="result-item result-success mb-3">
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1">
              <div class="font-weight-bold mb-2">
                {{ result.orderId }} - {{ result.recipient }}
              </div>
              <div class="mb-1"
                   v-if="hasValidTracking(result.trackingNo)">
                <strong>รหัสติดตามพัสดุ:</strong>
                <span class="badge badge-success">{{ result.trackingNo }}</span>
                <CopyButton :text="result.trackingNo"
                            class="ml-2" />
              </div>
              <div v-else
                   class="mb-1">
                <strong>รหัสติดตามพัสดุ:</strong>
                <span class="badge badge-warning">รอเลขพัสดุ</span>
                <small class="text-muted">(ระบบกำลังสร้าง)</small>
              </div>
              <div class="small text-muted">
                Proship OrderID: <code>{{ result.proshipId }}</code>
                <CopyButton :text="result.proshipId"
                            class="ml-2" />
              </div>
            </div>
          </div>
        </div>

        <!-- Export Excel Button -->
        <div class="alert text-center mt-4"
             v-if="ordersWithTracking.length > 0">
          <h5 class="mb-3">ส่งออกข้อมูล Excel</h5>
          <p class="mb-3">พร้อมส่งออก {{ ordersWithTracking.length }}/{{ successResults.length }} รายการที่มีรหัสติดตาม
          </p>
          <Button type="button"
                  variant="success"
                  size="lg"
                  @click="exportToExcel">
            ดาวน์โหลด Excel (Order ID + Tracking)
          </Button>
        </div>
      </div>

      <!-- Error Results -->
      <div v-if="errorResults.length > 0"
           class="mt-4">
        <h5 class="text-danger mb-3">ออเดอร์ที่เกิดข้อผิดพลาด</h5>
        <div v-for="(result, index) in errorResults"
             :key="'error-' + index"
             class="result-item result-error mb-3">
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1">
              <div class="font-weight-bold mb-2">
                📦 {{ result.orderId }} - {{ result.recipient }}
              </div>
              <div class="text-danger">
                ❌ ข้อผิดพลาด: {{ result.error }}
              </div>
            </div>
            <div class="ml-3">
              <span class="badge badge-danger badge-lg">❌ ผิดพลาด</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import Card from "@/components/core/Card";
import Button from "@/components/core/Button";
import CopyButton from "@/components/derived/CopyButton";
import { Toast } from "@/helpers/toastr";
import { exportOrdersToTiktokSofExcel, exportOrdersToTiktokSofDeliveredExcel, isValidTrackingNo } from "./helpers";

export default {
  name: "ResultsScreen",
  components: { Card, Button, CopyButton },
  props: {
    successResults: {
      type: Array,
      default: () => []
    },
    errorResults: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    totalResults() {
      return this.successResults.length + this.errorResults.length;
    },
    ordersWithTracking() {
      return this.successResults.filter(r => r && isValidTrackingNo(r.trackingNo));
    }
  },
  methods: {
    hasValidTracking(trackingNo) {
      return isValidTrackingNo(trackingNo);
    },

    exportToExcel() {
      if (this.ordersWithTracking.length === 0) {
        Toast('ไม่มีรายการที่มีรหัสติดตามให้ส่งออก', 'danger');
        return;
      }

      exportOrdersToTiktokSofExcel(this.ordersWithTracking);
      Toast(`ส่งออกสำเร็จ! ดาวน์โหลด ${this.ordersWithTracking.length} รายการแล้ว`, 'success');
    },

    exportDeliveredExcel() {
      if (this.ordersWithTracking.length === 0) {
        Toast('ไม่มีรายการที่มีรหัสติดตามให้ส่งออก', 'danger');
        return;
      }

      exportOrdersToTiktokSofDeliveredExcel(this.ordersWithTracking);
      Toast(`ส่งออกสำเร็จ! ดาวน์โหลด ${this.ordersWithTracking.length} รายการแล้ว`, 'success');
    }

  }
};
</script>

<style lang="scss" scoped>
.result-item {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 15px;
  background: white;

  &.result-success {
    background: #f0fff4;
    border-color: #9ae6b4;
  }

  &.result-error {
    background: #fed7d7;
    border-color: #feb2b2;
  }
}

.badge-lg {
  font-size: 1rem;
  padding: 0.5rem 1rem;
}

code {
  font-family: monospace;
  background: #f7fafc;
  padding: 2px 6px;
  border-radius: 3px;
}
</style>
